import React, { useEffect, useMemo, useState } from 'react';
import { FaEnvelope, FaFileAlt, FaSearch, FaSyncAlt, FaDownload, FaHandshake } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

type MaterialEvent = {
  id: string;
  event_type: string;
  email?: string | null;
  business_name?: string | null;
  source?: string | null;
  created_at: string;
  metadata?: Record<string, any> | null;
};

type Summary = {
  total_events: number;
  total_emails_sent: number;
  total_downloads: number;
  recent_events: MaterialEvent[];
};

const initialSummary: Summary = {
  total_events: 0,
  total_emails_sent: 0,
  total_downloads: 0,
  recent_events: [],
};

export default function AdminPartnerMaterials() {
  const [summary, setSummary] = useState<Summary>(initialSummary);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summary.recent_events;
    return summary.recent_events.filter((event) =>
      [event.event_type, event.email, event.business_name, event.source]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [summary.recent_events, search]);

  const loadSummary = async () => {
    setLoading(true);
    setMessage('');
    try {
      const { data, error } = await supabase.functions.invoke('partner-materials', {
        body: { action: 'admin_summary' },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'admin_summary_failed');
      setSummary(data.summary || initialSummary);
    } catch (error: any) {
      setMessage(error?.message || 'Impossible de charger les données.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const sendBrochure = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    try {
      const { data, error } = await supabase.functions.invoke('partner-materials', {
        body: {
          action: 'send_combo_brochure',
          email,
          business_name: businessName,
          source: 'admin_partner_materials',
        },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'send_failed');
      setMessage('Feuillet envoyé avec succès.');
      setEmail('');
      setBusinessName('');
      await loadSummary();
    } catch (error: any) {
      setMessage(error?.message || 'Impossible d’envoyer le feuillet.');
    }
  };

  const exportCsv = () => {
    const rows = [
      ['Date', 'Type', 'Email', 'Entreprise', 'Source'],
      ...filteredEvents.map((event) => [
        new Date(event.created_at).toLocaleString('fr-CA'),
        event.event_type,
        event.email || '',
        event.business_name || '',
        event.source || '',
      ]),
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `forfeo-feuillets-partenaires-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-cyan-700">
              <FaHandshake /> Matériel partenaire
            </div>
            <h1 className="mt-3 text-3xl font-black text-slate-950">Feuillets Combos Forfeo</h1>
            <p className="mt-2 max-w-3xl text-slate-600">
              Envoie le feuillet commercial aux marchands, suis les téléchargements et prépare la prospection des Combos collaboratifs.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSummary}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 font-bold text-slate-800 hover:bg-slate-50"
            >
              <FaSyncAlt className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 font-bold text-white hover:bg-slate-800"
            >
              <FaDownload /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-sm font-bold text-slate-500">Interactions totales</div>
          <div className="mt-2 text-4xl font-black text-slate-950">{summary.total_events}</div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-sm font-bold text-slate-500">Feuillets envoyés</div>
          <div className="mt-2 text-4xl font-black text-cyan-700">{summary.total_emails_sent}</div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-sm font-bold text-slate-500">Impressions / téléchargements</div>
          <div className="mt-2 text-4xl font-black text-amber-600">{summary.total_downloads}</div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <form onSubmit={sendBrochure} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <FaEnvelope className="text-cyan-700" />
            <h2 className="text-xl font-black text-slate-950">Envoyer un feuillet</h2>
          </div>
          <div className="space-y-3">
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Nom de l’entreprise"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none ring-cyan-200 focus:ring-4"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="courriel@entreprise.com"
              required
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none ring-cyan-200 focus:ring-4"
            />
            <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cyan-600 px-5 py-3 font-black text-white hover:bg-cyan-700">
              <FaEnvelope /> Envoyer
            </button>
            {message && <p className="text-sm font-bold text-slate-700">{message}</p>}
          </div>
        </form>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <FaFileAlt className="text-cyan-700" />
              <h2 className="text-xl font-black text-slate-950">Activité récente</h2>
            </div>
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher"
                className="w-full rounded-2xl border border-slate-200 py-3 pl-10 pr-4 outline-none ring-cyan-200 focus:ring-4 md:w-72"
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Entreprise</th>
                  <th className="px-4 py-3">Courriel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredEvents.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-3 text-slate-600">{new Date(event.created_at).toLocaleString('fr-CA')}</td>
                    <td className="px-4 py-3 font-bold text-slate-900">{event.event_type}</td>
                    <td className="px-4 py-3 text-slate-600">{event.business_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{event.email || '—'}</td>
                  </tr>
                ))}
                {!filteredEvents.length && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                      Aucune activité pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
